import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { FadeUp, MaskLine, SectionTag } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { SERVICES } from "@/data/services";

const PROCESS = [
  { n: "01", title: "Discover", body: "A 45-minute scoping call. We challenge the spec, define the bar, and agree on what 'great' looks like." },
  { n: "02", title: "Source", body: "Our warmed talent benches plus targeted headhunting. No job-board lottery." },
  { n: "03", title: "Screen", body: "Two rounds of our own interviews before you see anyone. First shortlist in 48 hours." },
  { n: "04", title: "Onboard", body: "Offer negotiation, notice-period management, and check-ins at 30/60/90 days." },
];

const ENGAGEMENTS = [
  {
    n: "01",
    title: "Contingent search",
    body: "Pay only when your candidate joins. Best for one-off critical roles where you want our bench working alongside yours.",
    fit: "Occasional hiring",
  },
  {
    n: "02",
    title: "Retained partnership",
    body: "A committed monthly engagement with priority sourcing and a named recruiter. Best for leadership mandates and confidential searches.",
    fit: "CXO & VP mandates",
  },
  {
    n: "03",
    title: "Embedded pod",
    body: "Our recruiters inside your Slack, your ATS, your brand — full RPO with SLAs and pipeline analytics. Best when hiring never really stops.",
    fit: "Continuous scale",
  },
];

export default function Services() {
  return (
    <main data-testid="services-page" className="pt-40">
      <Seo
        title="Staffing & HR Services — Samanvay HR"
        description="Permanent hiring, contract and IT staffing, RPO, payroll and statutory compliance, training, HR consulting and startup HR — one accountable team for every hiring problem."
      />
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <SectionTag>Services</SectionTag>
        <h1 className="font-display font-semibold tracking-tighter leading-[0.98] text-5xl sm:text-6xl lg:text-7xl text-slate-900 max-w-4xl">
          <MaskLine delay={0.15}>Every hiring problem,</MaskLine>
          <MaskLine delay={0.3}>
            <span className="font-editorial italic font-medium text-brand">one accountable team.</span>
          </MaskLine>
        </h1>
        <FadeUp delay={0.5} className="mt-8 max-w-2xl">
          <p className="text-lg text-slate-600 font-light leading-relaxed">
            Recruitment, staffing, payroll, compliance, training and HR consulting —
            a full people-operations stack so you can stay focused on the work itself.
          </p>
        </FadeUp>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {SERVICES.map((s, i) => (
            <FadeUp key={s.slug} delay={Math.min(i * 0.06, 0.3)} className={s.span}>
              <Link
                to={`/services/${s.slug}`}
                className="group block h-full rounded-3xl border border-slate-200/80 bg-white p-10 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-glow-sm"
                data-testid={`service-card-${s.slug}`}
              >
                <div className="w-12 h-12 rounded-2xl bg-orange-50 text-brand flex items-center justify-center border border-orange-200/50 transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                  <s.icon size={22} />
                </div>
                <h3 className="mt-6 font-display text-2xl font-semibold tracking-tight text-slate-900">{s.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.excerpt}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {s.tags.map((t) => (
                    <span key={t} className="rounded-full border border-slate-200/60 bg-slate-100 px-3 py-1 text-[11px] uppercase tracking-[0.15em] text-slate-500">
                      {t}
                    </span>
                  ))}
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="border-t border-slate-200 py-32" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6">
          <FadeUp>
            <SectionTag>The Method</SectionTag>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">Four steps. No black box.</h2>
          </FadeUp>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROCESS.map((p, i) => (
              <FadeUp key={p.n} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-8 transition-shadow duration-300 hover:shadow-glow-sm" data-testid={`process-step-${p.n}`}>
                  <p className="font-editorial italic text-5xl text-brand/80">{p.n}</p>
                  <h3 className="mt-5 font-display text-xl font-semibold text-slate-900">{p.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{p.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 py-32" data-testid="engagement-section">
        <div className="max-w-7xl mx-auto px-6">
          <FadeUp className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <SectionTag>Ways to Engage</SectionTag>
              <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05] max-w-2xl">
                Three doors in. Pick yours.
              </h2>
            </div>
            <p className="max-w-sm text-sm text-slate-500 leading-relaxed">
              Same bar, same recruiters — three commercial shapes depending on how often you hire
              and how deep you want us embedded.
            </p>
          </FadeUp>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
            {ENGAGEMENTS.map((e, i) => (
              <FadeUp key={e.n} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-slate-200/80 bg-white p-10 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-glow-sm" data-testid={`engagement-${i + 1}`}>
                  <p className="font-editorial italic text-4xl text-brand/80">{e.n}</p>
                  <h3 className="mt-4 font-display text-2xl font-semibold tracking-tight text-slate-900">{e.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{e.body}</p>
                  <p className="mt-6 inline-block rounded-full border border-brand/30 bg-orange-50/60 px-4 py-1.5 text-[11px] uppercase tracking-[0.15em] text-brand">
                    Best for: {e.fit}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 py-32">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FadeUp>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-slate-900 leading-[1.05]">
              Tell us the role — or the whole HR headache.{" "}
              <span className="font-editorial italic font-medium text-brand">We&rsquo;ll take it from here.</span>
            </h2>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                data-testid="services-cta-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 text-sm font-medium text-white transition-all duration-300 hover:bg-[#E63E12] hover:shadow-glow"
              >
                Start a search
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/industries"
                data-testid="services-industries-btn"
                className="group inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/70 px-8 py-4 text-sm font-medium text-slate-700 transition-colors duration-300 hover:border-brand/60 hover:text-brand"
              >
                See the industries we cover
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
